<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div>
    <v-container class="cronJobList">
      <v-row></v-row>
      <v-row>
        <v-data-table
          style="width: 100%;"
          :headers="headers"
          :items="itemList"
          :footer-props="{
            'items-per-page-options': [5, 10, 15, 25, -1],
          }"
          :items-per-page="25"
          :search="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          dense
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>Cron Jobs</v-toolbar-title>
              <v-spacer></v-spacer>
              <DynamicCronJobDialog
                v-if="editorType === 'dynamic'"
                :closeDialogBox.sync="closeDialogBox"
                @settingDialogVal="settingDialogVal"
                :baseCrudKey="baseCrudKey"
                :rowItem.sync="clickedItem"
              />
              <ExpectedTimeDialog
                v-else
                :closeDialogBox.sync="closeDialogBox"
                @settingDialogVal="settingDialogVal"
                :baseCrudKey="baseCrudKey"
                :rowItem.sync="clickedItem"
              />
              <v-btn
                class="mx-2" color="primary"
                fab icon small
                @click="newItem"
              >
                <v-icon>{{ icons.mdiPlus }}</v-icon>
              </v-btn>
            </v-toolbar>
          </template>
          <template v-slot:item.isActiveInactive="{ item }">
            <v-checkbox v-model="item.isActiveInactive" readonly></v-checkbox>
          </template>
          <template v-slot:item.destination="{ item }">
            <span class="caption">{{ item.destination }}</span>
          </template>
          <template v-slot:item.jobType="{ item }">
            <v-chip x-small :color="isLegacyJob(item) ? 'grey lighten-2' : 'blue lighten-4'">
              {{ isLegacyJob(item) ? 'Legacy' : 'Dynamic' }}
            </v-chip>
          </template>
          <template v-slot:item.lastRunTime="{ item }">
            {{ formatLastRun(item.lastRunTime) }}
          </template>
          <template v-slot:item.trigger="{ item }">
            <v-btn small @click="triggerNow(item)" :disabled="!item.isActiveInactive">
              Trigger Now
            </v-btn>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small class="mr-2" @click="openDialog(item)">{{ icons.mdiPencil }}</v-icon>
            <v-icon small @click="deleteItem(item)">{{ icons.mdiDelete }}</v-icon>
          </template>
        </v-data-table>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import { mdiPencil, mdiDelete, mdiPlus } from '@mdi/js';
import { describeSchedule } from '../../util/shared/cronSchedule';
import DynamicCronJobDialog from './DynamicCronJobDialog.vue';
import ExpectedTimeDialog from './ExpectedTimeDialog.vue';
import { clients } from '../../util/clients';
import { toCentralWallClockDateTime } from '../../util/shared/vue-global';

const { backendRest } = clients.direct;

// A job is "legacy" (catalog short-key) when its destination is not a URL path.
// Dynamic jobs always store a leading-slash URL destination.
const isLegacyJob = (item) => !(item && item.destination && item.destination.startsWith('/'));

export default {
  name: 'dynamicCronJobs',
  components: {
    DynamicCronJobDialog,
    ExpectedTimeDialog,
  },
  data() {
    return {
      search: '',
      sortBy: [''],
      sortDesc: [false],
      closeDialogBox: false,
      editorType: 'dynamic',
      clickedItem: {
        description: '',
        destination: '',
        params: {},
        expectedRunTime: {
          frequency: 'weekly', days: '*', monthDay: 'LW', hrs: 0, mins: 0, secs: 0,
        },
        isActiveInactive: false,
      },
      baseCrudKey: 'dynamicCronJobList',
      headers: [
        { text: 'Active/Inactive', value: 'isActiveInactive' },
        { text: 'Cron Job', value: 'description' },
        { text: 'Report URL', value: 'destination' },
        { text: 'Type', value: 'jobType', sortable: false },
        { text: 'Expected Time', value: 'nextExpectedRunTime' },
        { text: 'Last Run Time', value: 'lastRunTime' },
        { text: 'Trigger', value: 'trigger' },
        { text: 'Actions', value: 'actions' },
      ],
      icons: { mdiPencil, mdiDelete, mdiPlus },
    };
  },
  created() {
    this.loadList();
    this.getReportRoutes();
  },
  methods: {
    // The scheduler's Central wall-clock time, stored as if it were UTC - see
    // toCentralWallClockDateTime.
    formatLastRun(value) {
      return toCentralWallClockDateTime(value);
    },
    ...mapActions(['reAuth', 'flashWarn', 'flashSuccess']),
    ...mapActions('cron/cronJobUtil', ['getCronJobs', 'deleteCronJob', 'getReportRoutes']),
    ...mapMutations('base/crud', ['setCriteria', 'setItems', 'setItem']),
    isLegacyJob,
    openDialog(item) {
      // Legacy catalog-key jobs open the simple ExpectedTimeDialog (params are
      // baked into the backend catalog); URL jobs open the full dynamic editor.
      this.editorType = isLegacyJob(item) ? 'legacy' : 'dynamic';
      this.clickedItem = { ...item };
      this.setItem([this.baseCrudKey, this.clickedItem]);
      // Swap of editorType may remount the dialog; toggle false->true on the
      // next tick so the freshly mounted dialog's closeDialogBox watcher fires
      // and populates its local editable copy.
      this.closeDialogBox = false;
      this.$nextTick(() => { this.closeDialogBox = true; });
    },
    settingDialogVal(value) {
      this.closeDialogBox = value;
      this.loadList();
    },
    async loadList() {
      const list = await this.getCronJobs({ criteria: {}, baseCrudKey: this.baseCrudKey });
      this.convertExpectedRunTime(list);
      return list;
    },
    convertExpectedRunTime(list) {
      const itemList = [...(list || [])];
      for (let i = 0; i < itemList.length; i += 1) {
        // describeSchedule covers both cadences and pads the time itself; a weekly row still
        // renders exactly as it did before monthly existed (it delegates to describeCronDays).
        itemList[i].nextExpectedRunTime = describeSchedule(itemList[i].expectedRunTime);
      }
      this.setItems([this.baseCrudKey, itemList]);
      return itemList;
    },
    newItem() {
      this.clickedItem = {
        description: '',
        destination: '',
        params: {},
        expectedRunTime: {
          frequency: 'weekly', days: '*', monthDay: 'LW', hrs: 0, mins: 0, secs: 0,
        },
        isActiveInactive: false,
      };
      this.closeDialogBox = true;
      this.openDialog(this.clickedItem);
      // "New" always creates a URL-based dynamic job (legacy jobs are edit-only).
      this.editorType = 'dynamic';
    },
    async triggerNow(item) {
      let response = {};
      // Preserve the legacy penalty-calc special trigger path.
      if (item.destination === 'taxPenaltyCal') {
        const { jwt } = await this.reAuth();
        response = await backendRest.get(`/backend/pencalc?token=${jwt}`);
        return response;
      }
      response = await backendRest.post('/executeCronJob', { ...item });
      if (response.status === 200) {
        this.flashSuccess('Cron job triggered successfully');
        this.loadList();
      } else {
        this.flashWarn('Failed to trigger cron job');
      }
      return response;
    },
    deleteItem(item) {
      this.deleteCronJob({ itemId: item._id, baseCrudKey: this.baseCrudKey })
        .then(() => {
          this.flashSuccess('Cron job deleted successfully');
          this.loadList();
        })
        .catch((error) => {
          this.flashWarn(`Failed to delete cron job: ${error.message}`);
        });
    },
  },
  computed: {
    ...mapGetters('base/crud', ['criteria', 'items']),
    ...mapState({
      reportRoutes: (state) => (state.cron && state.cron.cronJobUtil ? state.cron.cronJobUtil.reportRoutes : []),
    }),
    allItems() {
      return this.items(this.baseCrudKey) || [];
    },
    itemList() {
      const routes = (this.reportRoutes || []).map((r) => r.url);
      // Union view: dynamic URL jobs + legacy catalog-key jobs. Only TR work
      // file exports are excluded (owned by their own tab).
      return this.allItems.filter((item) => {
        if (item.jobKind === 'trWorkFileExport') return false;
        // Legacy catalog-key jobs (non-URL destination, incl. blank).
        if (isLegacyJob(item)) return true;
        const dest = item.destination;
        return routes.some((pattern) => {
          if (pattern === dest) return true;
          if (!pattern.includes(':')) return false;
          const regexStr = pattern.replace(/:([^/]+)/g, '[^/]+');
          return new RegExp(`^${regexStr}$`).test(dest);
        });
      });
    },
  },
};
</script>
